import { useEffect, useRef, useState } from 'react';

interface PlayerDemoProps {
  title: string;
  cover: string;
}

const TOTAL_SECONDS = 120;

function formatTime(seconds: number) {
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

export default function PlayerDemo({ title, cover }: PlayerDemoProps) {
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [muted, setMuted] = useState(false);
  const intervalRef = useRef<number | null>(null);

  useEffect(() => {
    if (playing) {
      intervalRef.current = window.setInterval(() => {
        setProgress((prev) => {
          if (prev >= TOTAL_SECONDS) {
            setPlaying(false);
            return TOTAL_SECONDS;
          }
          return prev + 1;
        });
      }, 1000);
    }
    return () => {
      if (intervalRef.current) window.clearInterval(intervalRef.current);
    };
  }, [playing]);

  const percent = (progress / TOTAL_SECONDS) * 100;

  return (
    <div className="w-full rounded-lg overflow-hidden bg-foreground-950">
      <div className="relative w-full aspect-video bg-foreground-950">
        <img
          src={cover}
          alt={`${title}在线试看演示`}
          className={`w-full h-full object-cover object-top transition-opacity duration-500 ${playing ? 'opacity-40' : 'opacity-70'}`}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/80 via-transparent to-foreground-950/40"></div>

        <div className="absolute top-3 left-3 flex items-center gap-2">
          <span className="flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-background-50/15 text-background-50 text-xs backdrop-blur">
            <i className="ri-closed-captioning-line" aria-hidden="true"></i>
            中文字幕
          </span>
          <span className="px-2.5 py-1 rounded-md bg-accent-500 text-secondary-950 text-xs font-semibold">演示模式</span>
        </div>

        <button
          type="button"
          onClick={() => setPlaying((prev) => !prev)}
          className="absolute inset-0 flex items-center justify-center cursor-pointer"
          aria-label={playing ? '暂停演示' : '开始演示'}
        >
          <span className="w-16 h-16 md:w-20 md:h-20 flex items-center justify-center rounded-full bg-accent-500 text-secondary-950 shadow-lg hover:scale-105 transition-transform">
            <i className={playing ? 'ri-pause-fill text-3xl md:text-4xl' : 'ri-play-fill text-3xl md:text-4xl'} aria-hidden="true"></i>
          </span>
        </button>

        <div className="absolute bottom-0 left-0 right-0 px-3 md:px-4 pb-3 md:pb-4">
          <div className="h-1 w-full rounded-full bg-background-50/25 overflow-hidden mb-2">
            <div className="h-full rounded-full bg-accent-500 transition-all duration-500" style={{ width: `${percent}%` }}></div>
          </div>
          <div className="flex items-center justify-between gap-3 text-background-100 text-xs">
            <div className="flex items-center gap-3 md:gap-4">
              <button
                type="button"
                onClick={() => setPlaying((prev) => !prev)}
                className="hover:text-accent-400 cursor-pointer"
                aria-label={playing ? '暂停' : '播放'}
              >
                <i className={playing ? 'ri-pause-line text-lg' : 'ri-play-line text-lg'} aria-hidden="true"></i>
              </button>
              <button
                type="button"
                onClick={() => setMuted((prev) => !prev)}
                className="hover:text-accent-400 cursor-pointer"
                aria-label={muted ? '取消静音' : '静音'}
              >
                <i className={muted ? 'ri-volume-mute-line text-lg' : 'ri-volume-up-line text-lg'} aria-hidden="true"></i>
              </button>
              <span className="whitespace-nowrap">
                {formatTime(progress)} / {formatTime(TOTAL_SECONDS)}
              </span>
            </div>
            <div className="flex items-center gap-3 md:gap-4">
              <button type="button" className="hover:text-accent-400 cursor-pointer" aria-label="清晰度">
                <i className="ri-settings-3-line text-lg" aria-hidden="true"></i>
              </button>
              <button type="button" className="hover:text-accent-400 cursor-pointer" aria-label="全屏">
                <i className="ri-fullscreen-line text-lg" aria-hidden="true"></i>
              </button>
            </div>
          </div>
        </div>
      </div>
      <div className="px-4 py-3 bg-secondary-900 flex items-center gap-2">
        <i className="ri-information-line text-accent-400" aria-hidden="true"></i>
        <p className="text-xs text-background-300">
          当前为前端播放界面演示，不包含真实视频流，仅用于展示播放器布局与交互效果。
        </p>
      </div>
    </div>
  );
}